<script setup>
import {computed, onBeforeMount, watch, ref, onMounted} from 'vue'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useAppStore } from '@/stores/app'
import { useGeoStore } from '@/stores/geo'
import { useLayersStore } from '@/stores/layers'
import { useMapsStore } from '@/stores/maps'
import { useSplitStore } from '@/stores/split'
import { useMessagingStore } from '@/stores/messaging'
import { useSearchStore } from '@/stores/search'
import {useRouter, useRoute} from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import { useAuth } from '@/composables/useAuth'
import { useTheme } from '@/composables/useTheme'
import { getSafeRedirectPath } from '@/utils/auth/validation'
import { IMPORT_EXTENSIONS, fileExtension } from '@/composables/files'
import WsHandler from '@/services/wsHandler.vue'

import Toaster from '@/components/ui/toast/Toaster.vue'
import {useToast} from '@/components/ui/toast/use-toast'
import { AppNavigation } from '@/components/navigation'
import { Footer } from '@/components/footer'
import { MainMap, Navigator, MapSearch } from '@/components/map'
import BasemapStack from '@/components/maps/BasemapStack.vue'
import GlobalFeaturePopup from '@/components/feature/GlobalFeaturePopup.vue'
import DiscussionPopups from '@/components/discussion/DiscussionPopups.vue'
import { initDiscussionMap } from '@/components/discussion/discussionMap.js'
import { LayoutDialog } from "@/layouts"
import { SelectedFilters } from '@/components/filters'
import {ProjectList} from '@/components/project-list'
import ProjectActionsMenu from '@/components/project-list/ProjectActionsMenu.vue'
import ProjectMembersButton from '@/pages/Project/Components/ProjectMembersButton.vue'
import {Button} from '@/components/ui/button'
import {Ellipsis, PlusCircle} from 'lucide-vue-next'
import { useActiveTab } from '@/composables/useActiveTab'
import {MapsPanel} from '@/components/maps'
import MapGrid from '@/components/mapsplit/MapGrid.vue'
import MessagingPanel from '@/components/messaging/MessagingPanel.vue'
import {Reports} from '@/components/report'
import OrgPaymentsPanel from '@/components/payments/OrgPaymentsPanel.vue'
import { Spinner } from '@/components/ui/spinner'
import { SidebarPanel } from '@/components/ui/sidebar-panel'
import KYCPromptDialog from '@/components/bridge/KYCPromptDialog.vue'
import { usePageSize } from '@/composables/usePageSize'
import { useBackTarget } from '@/composables/useBackTarget'
import { useMapInset } from '@/composables/useMapInset'
import { useProjectInviteNotifications } from '@/composables/useProjectInviteNotifications'
import { bindS3SessionToOrg } from '@/services/s3Session'
import { bindGeoTokenToOrg } from '@/services/geoToken'
import {
  initFromUrl, hasPending, pendingTab, pendingSplit, pendingBasemaps, pendingBasemapConfig,
  markHydrated, setCurrentTab, scheduleWriteUrl,
} from '@/utils/mapUrlState'
const { toast } = useToast()
const { activeTab, navigateToTab } = useActiveTab()
const { effectiveSize } = usePageSize()
const { width: windowWidth } = useWindowSize()

const router = useRouter()
const route = useRoute()
const projectStore = useProjectStore()
const orgStore = useOrgStore()
const userStore = useUserStore()
const appStore = useAppStore()
const geoStore = useGeoStore()
const layersStore = useLayersStore()
const mapsStore = useMapsStore()
const splitStore = useSplitStore()
const messagingStore = useMessagingStore()
const searchStore = useSearchStore()
const { initAuthListener } = useAuth()
useTheme()
useProjectInviteNotifications()
initDiscussionMap(router)
const currentRoute = computed(() => route.name)

const isMobile = computed(() => windowWidth.value < 640)

const LIST_ROUTES = ['OrgProjects', 'OrgProjectPlaces', 'OrgLayers', 'OrgChats', 'OrgReportsPage']
const isListPage = computed(() => LIST_ROUTES.includes(route.name))
const isDetailRoute = computed(() => {
  if (isListPage.value) return false
  const layout = route.meta?.layout
  if (layout === 'modal' || layout === 'hide' || layout === 'fullscreen') return false
  return true
})

const isProjectRoute = computed(() => route.matched.some(r => r.name === 'Project'))

const { backTarget, goBack } = useBackTarget()

const createProject = () => {
  router.push({ name: 'CreateProject', params: { org_id: selectedOrgId.value } })
}

// Detail pages that declared a wider desktop layout (Settings, Members,
// Templates, ...) keep a wider panel; lists and feeds stay compact.
const panelSize = computed(() => {
  if (!isDetailRoute.value) return 'small'
  if (effectiveSize.value === 'fullpage') return 'full'
  if (effectiveSize.value === 'medium') return 'medium'
  return 'small'
})

const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => parseInt(route.params.org_id))
const member = computed(() => userStore.member)

const isLoggedIn = computed(() => appStore.isLoggedIn)
const isLoadingData = computed(() => appStore.isLoadingData)
const selectedOrgId = computed(() => orgStore.selectedOrg)
const showBackgroundUI = computed(() => route.meta.showBackgroundUI !== false)
const hasDates = computed(() => {
  const dates = layersStore.dates
  return dates && dates.length > 0
})
const { uiInset, canvasInset, bleed } = useMapInset()

// Project pages show the project they belong to under the page title, with its
// actions menu mirroring the back button on the other side of the header.
const currentProject = computed(() => {
  if (!projectId.value) return null
  const projects = projectStore.flatedProjects || []
  const found = projects.find(p => p.project_id === projectId.value)
  if (found) return found
  const current = projectStore.current
  return current?.project_id === projectId.value ? current : null
})

// The chats tab drives the panel header from the store rather than the route:
// an open conversation and the new-chat picker are both panel state, not pages.
const chatHeader = computed(() => {
  if (activeTab.value !== 'chats' || isDetailRoute.value) return null
  const conversation = messagingStore.activeConversation
  if (conversation) {
    return { title: conversation.peer.display_name, subtitle: conversation.peer.role || '' }
  }
  return messagingStore.newChatOpen ? { title: 'New chat', subtitle: '' } : null
})

// On a Place level the breadcrumbs in the header name the page, and need the room.
const panelTitle = computed(() => {
  if (route.name === 'OrgProjectPlaces') return ''
  return chatHeader.value?.title ?? (route.meta.title || '')
})
const currentOrg = computed(() => route.meta.orgSettings ? orgStore.current : null)

const panelSubtitle = computed(() =>
  chatHeader.value?.subtitle ?? (currentProject.value?.project_name || currentOrg.value?.organization_name || '')
)
const showPanelBack = computed(() => !!chatHeader.value || !!backTarget.value)

const handlePanelBack = () => {
  if (!chatHeader.value) return goBack()
  if (messagingStore.activeKey) messagingStore.setActive(null)
  else messagingStore.setNewChatOpen(false)
}

const isDragging = ref(false)

const handleDragEnter = (e) => {
  if (!isLoggedIn.value) return
  // The Chat tab owns its own drop zone for image attachments
  if (route.name === 'ProjectChat') return
  if (e.dataTransfer?.types?.includes('Files')) isDragging.value = true
}



onBeforeMount(async () => {
  initAuthListener(async (user) => {
    if (route.name === 'Invite' || route.name === 'MapInvite') {
      appStore.setAuthInitialized()
      return
    }

    if (user) {
      try {
        const result = await userStore.startSession(user)

        if (result.success) {
          const redirectPath = getSafeRedirectPath(route.query.redirect)
          if (redirectPath) {
            router.push(redirectPath)
            return
          }

          if (typeof currentRoute.value !== 'string' || (!currentRoute.value.startsWith('Auth') && !route.meta.isAuthPage)) return
          router.push({name: 'AuthOverview'})
        } else {
          if (result.error === 'USER_NOT_REGISTERED') {
            const redirectPath = getSafeRedirectPath(route.query.redirect)
            if (redirectPath) {
              router.push(redirectPath)
              return
            }
            if (route.name === 'SignUp') return
          }
          const pendingRedirect = getSafeRedirectPath(route.query.redirect)
          router.push(pendingRedirect
            ? { name: 'AuthOverview', query: { redirect: pendingRedirect } }
            : { name: 'AuthOverview' }
          )
        }
      } catch (e) {
        logOut()
      } finally {
        appStore.setAuthInitialized()
      }
    } else {
      // Only log out on a session transition — never during the initial unauthed render.
      if (appStore.isLoggedIn) {
        logOut()
      }
      appStore.setAuthInitialized()
    }
  })
})

const appHeight = () => document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`)

onMounted(async () => {
  await router.isReady()
  searchStore.setParams(route.query)

  window.addEventListener('resize', appHeight)
  appHeight()
})

watch(
  () => route.params.org_id,
  (newOrgId) => {
    if (newOrgId && newOrgId.toString() !== selectedOrgId.value?.toString()) {
      orgStore.setSelectedOrg(newOrgId.toString())
    }
  },
  { immediate: true }
)

bindS3SessionToOrg()
bindGeoTokenToOrg()

watch(
  () => route.query,
  (newQuery) => {
    if (Object.keys(newQuery).length > 0) {
      searchStore.setParams(newQuery)
    }
  },
  { deep: true }
)

// --- Shareable map-state hash (`m` param) -------------------------------------
// Parse the incoming link synchronously in setup — before any MapCanvas mounts —
// so per-instance camera restore (takePendingCamera) has the payload ready.
initFromUrl()

// Keep the module's notion of the active view in sync so writes from anywhere
// (incl. MapCanvas moveend) stamp the correct tab.
watch(activeTab, (tab) => setCurrentTab(tab), { immediate: true })

// Restore split layout + basemaps from an incoming link once the org's maps load.
// Camera is restored lazily by each MapCanvas as it mounts (takePendingCamera).
let tabRedirected = false
const hydrateFromUrl = () => {
  if (!hasPending()) return
  const orgId = selectedOrgId.value
  if (!orgId) return

  const tab = pendingTab()
  if (tab && tab !== activeTab.value && !tabRedirected) {
    tabRedirected = true
    navigateToTab(tab)
  }

  const split = pendingSplit()
  // A split payload needs the org's maps present before tiles resolve to panes.
  if (split && !(mapsStore.getMaps(orgId) || []).length) return

  if (split) {
    splitStore.setTiles({ orgId, tiles: split.t })
    splitStore.setArrangement({ orgId, arrangement: split.a })
    splitStore.setDividers({ orgId, x: split.d[0], y: split.d[1] })
    splitStore.setInitialized({ orgId })
    split.off.forEach(mapId => splitStore.setSync({ mapId, enabled: false }))
    Object.entries(pendingBasemaps()).forEach(([mapId, basemap]) =>
      mapsStore.updateMap({ orgId, mapId, patch: { basemap } })
    )
    // Restore per-tile basemap config into the layers store before the panes mount, so each
    // MapCanvas reads the shared PlanetScope mosaic / view toggles on first load.
    Object.entries(pendingBasemapConfig()).forEach(([mapId, cfg]) => {
      if (cfg.ps) layersStore.setPlanetscopeSelection({ layerId: mapId, selection: cfg.ps })
      if ('lbl' in cfg || 'bld' in cfg || 'ter' in cfg) {
        layersStore.setMapViewSettings({
          mapId,
          settings: { labels: cfg.lbl !== false, buildings: !!cfg.bld, terrain: !!cfg.ter },
        })
      }
    })
  }
  markHydrated()
}

watch(
  [selectedOrgId, () => (mapsStore.getMaps(selectedOrgId.value) || []).length],
  hydrateFromUrl,
  { immediate: true }
)

// Live-write the URL when the split layout or a map's basemap changes. Camera moves
// are already covered by MapCanvas's moveend hook.
watch(
  () => {
    const orgId = selectedOrgId.value
    if (!orgId) return ''
    const tiles = splitStore.getTiles(orgId)
    const div = splitStore.getDividers(orgId)
    const maps = mapsStore.getMaps(orgId) || []
    return JSON.stringify({
      t: tiles,
      a: splitStore.getArrangement(orgId),
      d: [div.x, div.y],
      off: tiles.filter(id => !splitStore.isSyncEnabled(id)),
      b: tiles.map(id => maps.find(m => m.id === id)?.basemap),
      ps: tiles.map(id => layersStore.getPlanetscopeSelection(id)),
      vs: tiles.map(id => layersStore.getMapViewSettings(id)),
    })
  },
  () => scheduleWriteUrl()
)

watch(
  () => member.value,
  async (value) => {
    if (value) {
      if (!zE) return
      zE('webWidget', 'identify', {
        name: value.display_name ? value.display_name : value.email,
        email: value.email
      })
    }
  }
)

const logOut = () => {
  appStore.logout()
}

const handleDrop = (e) => {
  e.preventDefault()
  e.stopPropagation()

  isDragging.value = false

  const files = e.dataTransfer.files

  if (!files || files.length === 0) {
    return
  }

  const anySupported = Array.from(files).some(f => IMPORT_EXTENSIONS.has(fileExtension(f.name)))

  if (!anySupported) {
    toast({ title: "Unsupported file format", variant: 'destructive' })
    return
  }

  geoStore.setFiles(files)

  if (route.name === 'FileViewer' || route.name === 'ProjectImport') return

  const orgId = route.params.org_id || orgStore.selectedOrg
  // Drops inside a project skip the picker gate by landing on ProjectImport.
  if (route.params.project_id) {
    router.push({
      name: 'ProjectImport',
      params: { org_id: orgId, project_id: route.params.project_id }
    })
  } else {
    router.push({ name: 'FileViewer', params: { org_id: orgId } })
  }
}
</script>

<template>
<div class="flex h-screen">
  <AppNavigation v-if="isLoggedIn && showBackgroundUI" />

  <div class="flex-1 flex flex-col min-w-0">
    <ws-handler v-if="isLoggedIn && showBackgroundUI" />
    <GlobalFeaturePopup v-if="isLoggedIn" />
    <DiscussionPopups v-if="isLoggedIn" />

    <main
      class="flex-1 z-40 text-sm main-section relative"
      role="main"
      aria-label="Main application content"
      id="main-content"
      :class="{ 'no-background-ui': !showBackgroundUI }"
      @dragenter="handleDragEnter"
    >
      <!-- z-40 so shadcn dialogs (z-50) still receive pointer events mid-drag. -->
      <div
        v-if="isLoggedIn && isDragging"
        @dragover.prevent
        @drop.stop.prevent="handleDrop"
        @dragleave="isDragging = false"
        class="absolute inset-0 z-40 bg-gray-600/20"
        role="region"
        aria-label="File drop zone"
        aria-describedby="drop-zone-description"
      >
        <span id="drop-zone-description" class="sr-only">
          Drop supported files here (KML, KMZ, GeoJSON, images, GPX, FIT, Shapefile)
        </span>
      </div>

      <div v-if="$route.meta.layout === 'fullscreen'" class="h-full w-full overflow-auto">
        <router-view />
      </div>

      <div v-else-if="showBackgroundUI && activeTab === 'payments'" class="h-full w-full bg-background flex flex-col overflow-hidden">
        <OrgPaymentsPanel :key="String($route.params.org_id)" />
      </div>

      <div v-else-if="showBackgroundUI" class="h-full w-full">
        <LayoutDialog v-if="$route.meta.layout === 'modal'" />
        <div v-else-if="$route.meta.layout === 'hide'"></div>
        <div
          v-else
          class="h-full w-full relative"
          :style="{
            '--map-inset': `${canvasInset}px`,
            '--map-bleed': `${bleed}px`,
            '--map-ui-inset': `${uiInset}px`,
          }"
        >
          <!-- The canvas box stops one bleed short of the panel card's right
               edge, so the card's border + rounded corners overlay live map.
               Anything drawn ON the map adds --map-bleed back to clear it. -->
          <div class="absolute inset-0" :style="{ left: 'var(--map-inset, 0px)' }">
            <MapGrid v-if="isLoggedIn && activeTab === 'layers'" />
            <MainMap :active-tab="activeTab" v-else-if="isLoggedIn" />
          </div>

          <div
            v-if="isLoadingData"
            class="absolute z-50 top-14 -translate-x-1/2 bg-background rounded-full p-2 shadow-lg"
            :style="{ left: 'calc(50% + var(--map-ui-inset, 0px) / 2)' }"
            role="status"
            aria-live="polite"
            aria-label="Loading data"
            data-testid="loading-indicator"
          >
            <Spinner class="w-6 h-6" aria-hidden="true" />
            <span class="sr-only">Loading data...</span>
          </div>

          <div class="absolute top-2 z-30 pointer-events-auto flex flex-col items-end gap-2"
            :class="isMobile ? 'left-2 right-2' : 'right-2'">
            <SelectedFilters v-if="projectId" />
            <div class="flex items-center gap-2" :class="{ 'w-full': isMobile }">
              <MapSearch :full-width="isMobile" :class="{ 'flex-1 min-w-0': isMobile }" />
              <BasemapStack v-if="activeTab !== 'layers'" />
            </div>
          </div>

          <!-- Overlay column: below 1024px SidebarPanel positions itself as a
               fixed drawer and escapes this container; at desktop widths it
               renders in-flow and needs this absolute wrapper to sit over the map. -->
          <div class="absolute inset-2 z-30 pointer-events-none flex flex-col">
            <div class="flex-1 min-h-0 flex">
              <SidebarPanel
                :title="panelTitle"
                :subtitle="panelSubtitle"
                :size="panelSize"
                :show="true"
                :show-close="false"
                :show-back="showPanelBack"
                @back="handlePanelBack"
              >
                <template #title-leading>
                  <span v-if="chatHeader" id="panel-title-leading" class="flex items-center" />
                </template>
                <template #leading>
                  <span id="panel-leading" class="flex min-w-0 flex-1 items-center" />
                </template>
                <template #actions>
                  <span id="panel-actions" class="flex items-center" />
                  <ProjectMembersButton v-if="currentProject && isProjectRoute" class="mr-1.5" />
                  <ProjectActionsMenu v-if="currentProject && isProjectRoute" :project="currentProject">
                    <template #trigger>
                      <Button
                        variant="secondary"
                        class="w-8 h-8 p-0 rounded-full"
                        :aria-label="`Actions for ${currentProject.project_name}`"
                        title="Project actions"
                        type="button"
                      >
                        <Ellipsis class="w-5 h-5" />
                      </Button>
                    </template>
                  </ProjectActionsMenu>
                  <Button
                    v-else-if="activeTab === 'projects' && !isDetailRoute"
                    variant="secondary"
                    size="sm"
                    type="button"
                    @click="createProject"
                  >
                    <PlusCircle class="w-4 h-4 mr-1.5" aria-hidden="true" />
                    <span>New Project</span>
                  </Button>
                </template>

                <div class="h-full flex flex-col">
                  <router-view v-if="isDetailRoute" v-slot="{ Component }">
                    <component :is="Component" />
                  </router-view>
                  <template v-else>
                    <ProjectList v-if="activeTab === 'projects'" />
                    <MapsPanel v-else-if="activeTab === 'layers'" :key="activeTab" />
                    <MessagingPanel v-else-if="activeTab === 'chats'" :key="activeTab" />
                    <Reports v-else-if="activeTab === 'reports'" :key="activeTab" />
                  </template>
                </div>
              </SidebarPanel>
            </div>

            <!-- Timeline + footer, offset past the panel so the panel (fixed,
                 z-40) doesn't cover them. -->
            <div class="mt-2 flex flex-col items-start" :style="{ marginLeft: `${uiInset}px` }">
              <div
                v-if="isLoggedIn && hasDates"
                class="pointer-events-auto w-full lg:w-3/5"
                role="navigation"
                aria-label="Map navigation controls"
              >
                <Navigator />
              </div>

              <div
                class="hidden lg:block pointer-events-auto mt-2 rounded-xl bg-background/90 backdrop-blur shadow-lg px-3 py-1.5"
                role="contentinfo"
                aria-label="Application footer"
              >
                <Footer compact />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="h-full w-full">
        <LayoutDialog v-if="$route.meta.layout === 'modal'" />
        <div v-else-if="$route.meta.layout === 'hide'"></div>
        <router-view v-else />
      </div>
    </main>

  </div>
</div>
<Toaster />
<KYCPromptDialog />
</template>

<style>
.main-section {
  max-height: var(--app-height);
  min-height: var(--app-height);
}

.main-section.no-background-ui {
  max-height: var(--app-height);
  min-height: var(--app-height);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
